import { useState, useContext } from "react";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import GlobalContext from "../../context/GlobalContext";
import Navbar from "../../components/shared/Navbar";
import NavbarWeb from "../../components/NavbarWeb";

export default function Index() {
  const [isYearly, setIsYearly] = useState(true);
  const { userInfo } = useContext(GlobalContext);

  const priceIds = {
    basic_month: "price_1AyW5QRmaDbhm5jMTPNoOkF",
    basic_year: "price_1RsvHwQRmaDbhm5jx28pqD1w",
    standard_month: "price_1RsvEYQRmaDbhm5jCuP4AcCj",
    standard_year: "price_1RsvHhQRmaDbhm5jRSkSm5F6",
    plus_month: "price_1RsvEoQRmaDbhm5j3LnFYtv4",
    plus_year: "price_1RsvGsQRmaDbhm5jPRA948qa",
    pro_month: "price_1RsvFPQRmaDbhm5jr2oAe6DJ",
    pro_year: "price_1RsvFlQRmaDbhm5jBUOlMeEv",
  };

  const plans = [
    {
      title: "Basic",
      price: isYearly ? "80" : "8",
      period: isYearly ? "year" : "month",
      description: "Start your journey—simple and effective!",
      features: ["Access to all basic features", "5 Documents + size"],
      bgGradient: "from-emerald-400 to-teal-500",
      popular: false,
    },
    {
      title: "Standard",
      price: isYearly ? "120" : "12",
      period: isYearly ? "year" : "month",
      description: "Step up your game with added value!",
      features: ["Access to all basic features", "12 Documents"],
      bgGradient: "from-emerald-500 to-teal-600",
      popular: false,
    },
    {
      title: "Plus",
      price: isYearly ? "240" : "24",
      period: isYearly ? "year" : "month",
      description: "Upload more documents for a better experience!",
      features: ["Access to all basic features", "25 Documents"],
      bgGradient: "from-emerald-600 to-teal-700",
      popular: true,
    },
    {
      title: "Family", // maps to "Pro" in backend
      price: isYearly ? "490" : "49",
      period: isYearly ? "year" : "month",
      description: "Experience the ultimate with all-inclusive perks!",
      features: ["Access to all basic features", "25+ Documents"],
      bgGradient: "from-emerald-800 to-teal-900",
      popular: false,
    },
  ];

  // Helper to get correct price ID
  const getPriceId = (planTitle) => {
    const key =
      planTitle.toLowerCase() === "family"
        ? `pro_${isYearly ? "year" : "month"}`
        : `${planTitle.toLowerCase()}_${isYearly ? "year" : "month"}`;
    return priceIds[key];
  };

  const handlePlanClick = (planTitle) => {
    const priceId = getPriceId(planTitle);
    if (userInfo) {
      // already logged in → go straight to checkout
      window.location.href = `/checkout?price_id=${priceId}`;
    } else {
      // not logged in → go to login and pass redirect
      window.location.href = `/login?next=/checkout?price_id=${priceId}`;
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 to-emerald-50 min-h-screen">
      <NavbarWeb />
      {/* Main Content */}
      <main className="mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 max-w-7xl">
        {/* Hero Section */}
        <div className="mb-12 text-center">
          <h2 className="mb-4 font-bold text-emerald-700 text-3xl sm:text-4xl lg:text-5xl">
            Choose Your Plan
          </h2>
          <p className="mx-auto max-w-2xl text-gray-600 text-lg sm:text-xl">
            Select the perfect plan for your medical documentation needs
          </p>
        </div>

        {/* Pricing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-white shadow-sm p-1 border border-emerald-200 rounded-full">
            <div className="flex">
              <button
                onClick={() => setIsYearly(false)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  !isYearly
                    ? "bg-emerald-600 text-white shadow-md"
                    : "text-emerald-600 hover:text-emerald-700"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsYearly(true)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 relative ${
                  isYearly
                    ? "bg-emerald-600 text-white shadow-md"
                    : "text-emerald-600 hover:text-emerald-700"
                }`}
              >
                Yearly
              </button>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="flex flex-col items-center gap-12">
          {/* Top row: Free, Basic, Standard */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-center">
            {/* Free Plan Card */}
            <div className="relative bg-white rounded-2xl shadow-md border border-emerald-100 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl">
              <div className="bg-emerald-50 rounded-t-2xl p-6 text-emerald-900">
                <h3 className="mb-2 font-bold text-xl">Free Plan</h3>
                <div className="mb-3">
                  <span className="font-extrabold text-3xl">$0</span>
                  <span className="text-emerald-800">/lifetime</span>
                </div>
                <p className="text-sm text-emerald-700">
                  Limited access to explore MedTag
                </p>
              </div>
              <div className="p-6">
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <div className="flex-shrink-0 bg-emerald-100 mt-0.5 p-1 rounded-full">
                      <Check className="w-3 h-3 text-emerald-600" />
                    </div>
                    <span className="text-gray-700 text-sm leading-relaxed">
                      Access to basic features
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="flex-shrink-0 bg-emerald-100 mt-0.5 p-1 rounded-full">
                      <Check className="w-3 h-3 text-emerald-600" />
                    </div>
                    <span className="text-gray-700 text-sm leading-relaxed">
                      2 Documents only
                    </span>
                  </li>
                </ul>
                <span className="block text-center w-full py-3 px-4 rounded-xl font-semibold text-sm bg-emerald-100 text-emerald-600 cursor-default">
                  Current Plan
                </span>
              </div>
            </div>

            {plans
              .filter(
                (plan) => plan.title === "Basic" || plan.title === "Standard"
              )
              .map((plan) => (
                <div
                  key={plan.title}
                  className={`relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 ${
                    plan.popular ? "ring-2 ring-emerald-500 scale-105" : ""
                  }`}
                >
                  <div
                    className={`bg-gradient-to-r ${plan.bgGradient} rounded-t-2xl p-6 text-white`}
                  >
                    <h3 className="mb-2 font-bold text-xl">{plan.title}</h3>
                    <div className="mb-3">
                      <span className="font-extrabold text-3xl">
                        ${plan.price}
                      </span>
                      <span className="text-emerald-100">/{plan.period}</span>
                    </div>
                    <p className="text-emerald-100 text-sm leading-relaxed">
                      {plan.description}
                    </p>
                  </div>
                  <div className="p-6">
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="flex-shrink-0 bg-emerald-100 mt-0.5 p-1 rounded-full">
                            <Check className="w-3 h-3 text-emerald-600" />
                          </div>
                          <span className="text-gray-700 text-sm leading-relaxed">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handlePlanClick(plan.title)}
                      className={`block text-center w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200 ${
                        plan.popular
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-md hover:shadow-lg"
                          : "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200"
                      }`}
                    >
                      {plan.popular ? "Get Started" : "Choose Plan"}
                    </button>
                  </div>
                </div>
              ))}
          </div>

          {/* Bottom row: Plus, Family */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 justify-center">
            {plans
              .filter(
                (plan) => plan.title === "Plus" || plan.title === "Family"
              )
              .map((plan) => (
                <div
                  key={plan.title}
                  className={`relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 ${
                    plan.popular ? "ring-2 ring-emerald-500 scale-105" : ""
                  }`}
                >
                  {plan.popular && (
                    <div className="top-0 left-1/2 absolute bg-emerald-500 px-4 py-1 rounded-full font-semibold text-white text-xs -translate-x-1/2 -translate-y-1/2 transform">
                      Most Popular
                    </div>
                  )}
                  <div
                    className={`bg-gradient-to-r ${plan.bgGradient} rounded-t-2xl p-6 text-white`}
                  >
                    <h3 className="mb-2 font-bold text-xl">{plan.title}</h3>
                    <div className="mb-3">
                      <span className="font-extrabold text-3xl">
                        ${plan.price}
                      </span>
                      <span className="text-emerald-100">/{plan.period}</span>
                    </div>
                    <p className="text-emerald-100 text-sm leading-relaxed">
                      {plan.description}
                    </p>
                  </div>
                  <div className="p-6">
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="flex-shrink-0 bg-emerald-100 mt-0.5 p-1 rounded-full">
                            <Check className="w-3 h-3 text-emerald-600" />
                          </div>
                          <span className="text-gray-700 text-sm leading-relaxed">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handlePlanClick(plan.title)}
                      className={`block text-center w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200 ${
                        plan.popular
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-md hover:shadow-lg"
                          : "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200"
                      }`}
                    >
                      {plan.popular ? "Get Started" : "Choose Plan"}
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 shadow-xl mt-16 p-8 rounded-2xl text-white text-center">
          <h3 className="mb-4 font-bold text-2xl">Need a custom solution?</h3>
          <p className="mb-6 text-emerald-100">
            Contact our team to discuss enterprise plans and custom pricing
          </p>
          <Link
            to="/contact"
            className="bg-white hover:bg-emerald-50 shadow-md hover:shadow-lg px-8 py-3 rounded-xl font-semibold text-emerald-600 transition-all duration-200"
          >
            Contact Sales
          </Link>
        </div>
      </main>
    </div>
  );
}
